import React, { useState, useEffect, useMemo } from 'react';
import { Input, InputNumber, Radio, Select, Modal, notification } from 'antd';
import { PlusOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { ThemeModal } from '../Styles/Theme';
import {
  QuotaModalContent,
  QuotaEntryRow,
  QuotaRangeBandRow,
  QuotaChildrenBandRow,
  QuotaTotalRow,
  QuotaActions,
} from './CreateCampaignStyle';

// Field configuration - maps field names to PS qualification codes and types
const FIELD_CONFIG = {
  gender: { code: 211, type: 'condition_codes', label: 'Gender' },
  age: { code: 212, type: 'range_sets', units: 311, label: 'Age' },
  income: { code: 213, type: 'range_sets', units: 321, label: 'Income' },
  race: { code: 244, type: 'condition_codes', label: 'Race / Ethnicity' },
  hispanic_origin: { code: 245, type: 'condition_codes', label: 'Hispanic Origin' },
  relationship: { code: 217, type: 'condition_codes', label: 'Relationship Status' },
  children: { code: 232, type: 'condition_codes', label: 'Children' },
  employment: { code: 215, type: 'condition_codes', label: 'Employment' },
  education: { code: 216, type: 'condition_codes', label: 'Education' },
  device: { code: 219, type: 'condition_codes', label: 'Device' },
  region: { code: 223, type: 'condition_codes', label: 'Region' },
  division: { code: 224, type: 'condition_codes', label: 'Division' },
  state: { code: 225, type: 'condition_codes', label: 'State' },
  msa: { code: 227, type: 'condition_codes', label: 'MSA / CBSA' },
  csa: { code: 226, type: 'condition_codes', label: 'CSA' },
  county: { code: 228, type: 'condition_codes', label: 'County' },
  dma: { code: 231, type: 'condition_codes', label: 'DMA' },
  zipcode: { code: 229, type: 'condition_codes', label: 'Zipcode' },
};

const QuotaModal = ({
  visible,
  onClose,
  onSave,
  field, // 'gender', 'age', 'income', 'at_1031', etc.
  selectedValues, // Array of selected values for this field (from form)
  demographicOptions, // All options for this field from API
  existingQuota, // Existing quota data if editing
  totalResponses, // Total responses for validation
  ageRange, // For age field: { min, max }
  incomeRange, // For income field: { min, max }
  atQuestionName, // For AT fields: the question/qualification name
}) => {
  const [entries, setEntries] = useState([]);
  const [searchText, setSearchText] = useState('');

  // Resolve field config - supports both fixed fields and dynamic at_* fields
  const fieldConfig = useMemo(() => {
    if (!field) return null;
    if (FIELD_CONFIG[field]) return FIELD_CONFIG[field];
    // Handle at_* fields (Advanced Targeting)
    const atMatch = field.match(/^at_(\d+)$/);
    if (atMatch) {
      return {
        code: parseInt(atMatch[1], 10),
        type: 'condition_codes',
        label: atQuestionName || `AT Question ${atMatch[1]}`,
      };
    }
    return null;
  }, [field, atQuestionName]);
  const isRangeField = fieldConfig?.type === 'range_sets';
  const isChildrenField = field === 'children';

  // Get the parent range bounds
  const parentRange = useMemo(() => {
    if (field === 'age' && ageRange) return { min: ageRange.min, max: ageRange.max };
    if (field === 'income' && incomeRange) return { min: incomeRange.min, max: incomeRange.max };
    return null;
  }, [field, ageRange, incomeRange]);

  // Determine which children options are selected
  const hasHaveChildren = useMemo(() => {
    if (!isChildrenField) return false;
    return selectedValues.some(v => {
      const opt = demographicOptions.find(o => o.value === v);
      return opt && String(opt.condition_code) !== '167';
    });
  }, [isChildrenField, selectedValues, demographicOptions]);

  const hasNoChildren = useMemo(() => {
    if (!isChildrenField) return false;
    return selectedValues.some(v => {
      const opt = demographicOptions.find(o => o.value === v);
      return opt && String(opt.condition_code) === '167';
    });
  }, [isChildrenField, selectedValues, demographicOptions]);

  // Initialize entries when modal opens
  useEffect(() => {
    if (!visible || !field) return;

    if (existingQuota && existingQuota.entries?.length > 0) {
      // Editing existing quota
      setEntries(existingQuota.entries.map(e => ({
        ...e,
        count: e.count || 0,
      })));
    } else if (isChildrenField) {
      // Children field: create entries based on selected options
      const newEntries = [];
      if (hasHaveChildren) {
        newEntries.push({
          type: 'have_children',
          range_from: null,
          range_to: null,
          units: 311, // years by default
          gender: 'either',
          label: 'Have Children',
          count: 0,
        });
      }
      if (hasNoChildren) {
        newEntries.push({
          type: 'no_children',
          condition_code: 167,
          label: 'No Children',
          count: 0,
        });
      }
      setEntries(newEntries);
    } else if (isRangeField && parentRange) {
      // For range fields, start with one band covering the full range
      setEntries([{
        range_from: parentRange.min,
        range_to: parentRange.max,
        units: fieldConfig.units,
        count: 0,
      }]);
    } else if (!isRangeField) {
      // For condition_codes fields, create entry for each selected value
      const newEntries = selectedValues.map(value => {
        const option = demographicOptions.find(opt => opt.value === value);
        return {
          condition_code: option?.condition_code || value,
          label: option?.label || value,
          count: 0,
        };
      });
      setEntries(newEntries);
    }
  }, [visible, field, selectedValues, demographicOptions, existingQuota, isRangeField, isChildrenField, hasHaveChildren, hasNoChildren, parentRange, fieldConfig]);

  // Reset when modal closes
  useEffect(() => {
    if (!visible) {
      setEntries([]);
      setSearchText('');
    }
  }, [visible]);

  // Calculate total
  const totalCount = useMemo(() => {
    return entries.reduce((sum, entry) => sum + (entry.count || 0), 0);
  }, [entries]);

  // Check if total exceeds responses
  const exceedsTotal = totalResponses && totalCount > totalResponses;
  const isUnderTotal = totalResponses && totalCount < totalResponses;

  // Filtered entries for search (condition_codes fields only)
  const showSearch = !isRangeField && !isChildrenField && entries.length > 15;
  const displayEntries = useMemo(() => {
    if (!searchText || isRangeField || isChildrenField) return entries.map((e, i) => ({ ...e, _idx: i }));
    const lower = searchText.toLowerCase();
    return entries
      .map((e, i) => ({ ...e, _idx: i }))
      .filter(e => e.label?.toLowerCase().includes(lower));
  }, [entries, searchText, isRangeField, isChildrenField]);

  const handleCountChange = (index, value) => {
    setEntries(prev => {
      const newEntries = [...prev];
      newEntries[index] = { ...newEntries[index], count: value || 0 };
      return newEntries;
    });
  };

  // Range band handlers
  const handleRangeFromChange = (index, value) => {
    setEntries(prev => {
      const newEntries = [...prev];
      newEntries[index] = { ...newEntries[index], range_from: value };
      return newEntries;
    });
  };

  const handleRangeToChange = (index, value) => {
    setEntries(prev => {
      const newEntries = [...prev];
      newEntries[index] = { ...newEntries[index], range_to: value };
      // Auto-chain: update next band's "from" to value + 1
      if (value != null && index + 1 < newEntries.length) {
        newEntries[index + 1] = { ...newEntries[index + 1], range_from: value + 1 };
      }
      return newEntries;
    });
  };

  const handleAddBand = () => {
    setEntries(prev => {
      const lastBand = prev[prev.length - 1];
      const nextFrom = lastBand?.range_to != null ? lastBand.range_to + 1 : null;
      return [
        ...prev,
        {
          range_from: nextFrom,
          range_to: parentRange?.max || null,
          units: fieldConfig.units,
          count: 0,
        },
      ];
    });
  };

  const handleRemoveBand = (index) => {
    setEntries(prev => {
      const newEntries = prev.filter((_, i) => i !== index);
      // Re-chain: update the band that now occupies this position
      if (index > 0 && index < newEntries.length && newEntries[index - 1]?.range_to != null) {
        newEntries[index] = { ...newEntries[index], range_from: newEntries[index - 1].range_to + 1 };
      }
      return newEntries;
    });
  };

  // Children band handlers
  const handleChildrenBandChange = (index, fieldName, value) => {
    setEntries(prev => {
      const newEntries = [...prev];
      newEntries[index] = { ...newEntries[index], [fieldName]: value };
      return newEntries;
    });
  };

  const handleAddChildrenBand = () => {
    setEntries(prev => {
      // Insert before the "no_children" entry if it exists
      const currentUnit = prev.find(e => e.type === 'have_children')?.units || 311;
      const noChildrenIdx = prev.findIndex(e => e.type === 'no_children');
      const newBand = {
        type: 'have_children',
        range_from: null,
        range_to: null,
        units: currentUnit,
        gender: 'either',
        label: 'Have Children',
        count: 0,
      };
      if (noChildrenIdx >= 0) {
        const updated = [...prev];
        updated.splice(noChildrenIdx, 0, newBand);
        return updated;
      }
      return [...prev, newBand];
    });
  };

  const handleRemoveChildrenBand = (index) => {
    setEntries(prev => prev.filter((_, i) => i !== index));
  };

  // Unit change handler with confirmation when age values exist
  const handleUnitChange = (newUnit) => {
    const currentUnit = entries.find(e => e.type === 'have_children')?.units || 311;
    if (currentUnit === newUnit) return;

    const hasAgeValues = entries.some(e =>
      e.type === 'have_children' && (e.range_from != null || e.range_to != null)
    );

    if (hasAgeValues) {
      Modal.confirm({
        title: 'Change Unit',
        content: 'Modifying the Unit Name will delete the quotas.',
        okText: 'OK',
        cancelText: 'Cancel',
        onOk: () => {
          setEntries(prev => prev.map(e =>
            e.type === 'have_children'
              ? { ...e, units: newUnit, range_from: null, range_to: null, count: 0 }
              : e
          ));
        },
      });
    } else {
      setEntries(prev => prev.map(e =>
        e.type === 'have_children' ? { ...e, units: newUnit } : e
      ));
    }
  };

  const handleEvenSplit = () => {
    if (!totalResponses || entries.length === 0) return;

    const countPerEntry = Math.floor(totalResponses / entries.length);
    const remainder = totalResponses % entries.length;

    setEntries(prev => prev.map((entry, index) => ({
      ...entry,
      count: countPerEntry + (index < remainder ? 1 : 0),
    })));
  };

  const handleClearAll = () => {
    setEntries(prev => prev.map(entry => ({ ...entry, count: 0 })));
  };

  // Validate range bands for overlaps and invalid ranges
  const rangeErrors = useMemo(() => {
    if (!isRangeField) return [];
    const errors = [];

    entries.forEach((entry, index) => {
      // Check from < to
      if (entry.range_from != null && entry.range_to != null && entry.range_from >= entry.range_to) {
        errors.push({ index, message: `Band ${index + 1}: "From" must be less than "To"` });
      }
      // Check within parent range bounds
      if (parentRange) {
        if (entry.range_from != null && entry.range_from < parentRange.min) {
          errors.push({ index, message: `Band ${index + 1}: "From" cannot be less than ${parentRange.min}` });
        }
        if (entry.range_to != null && entry.range_to > parentRange.max) {
          errors.push({ index, message: `Band ${index + 1}: "To" cannot exceed ${parentRange.max}` });
        }
      }
    });

    // Check for overlapping ranges
    const validEntries = entries
      .map((e, i) => ({ ...e, originalIndex: i }))
      .filter(e => e.range_from != null && e.range_to != null);

    const sorted = [...validEntries].sort((a, b) => a.range_from - b.range_from);
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i].range_to > sorted[i + 1].range_from) {
        errors.push({
          index: sorted[i + 1].originalIndex,
          message: 'Overlapping ranges detected',
        });
      }
    }

    return errors;
  }, [entries, isRangeField, parentRange]);

  const hasRangeErrors = rangeErrors.length > 0;

  // Validate children bands
  const childrenErrors = useMemo(() => {
    if (!isChildrenField) return [];
    const errors = [];
    const haveChildrenBands = entries.filter(e => e.type === 'have_children');

    haveChildrenBands.forEach((entry, bandIdx) => {
      const realIdx = entries.indexOf(entry);
      // Validate range whenever both values are filled (regardless of count)
      if (entry.range_from != null && entry.range_to != null && entry.range_from > entry.range_to) {
        errors.push({ index: realIdx, message: `Band ${bandIdx + 1}: "From" must be less than or equal to "To"` });
      }
      // If count > 0, range must be filled
      if (entry.count > 0 && (entry.range_from == null || entry.range_to == null)) {
        errors.push({ index: realIdx, message: `Band ${bandIdx + 1}: Age range is required` });
      }
    });

    // Check for overlapping age ranges between bands with the SAME gender.
    // PS allows different genders to overlap (Boy + Girl + Either with same range is fine),
    // but flags same-gender bands with overlapping ages (Boy 1-11 + Boy 1-10 = error).
    const bandsWithRanges = haveChildrenBands
      .map((entry, bandIdx) => ({
        entry,
        bandIdx,
        realIdx: entries.indexOf(entry),
      }))
      .filter(b => b.entry.range_from != null && b.entry.range_to != null && b.entry.range_from <= b.entry.range_to);

    for (let i = 0; i < bandsWithRanges.length; i++) {
      for (let j = i + 1; j < bandsWithRanges.length; j++) {
        const a = bandsWithRanges[i].entry;
        const b = bandsWithRanges[j].entry;

        // Only check overlap when genders are exactly the same (boy-boy, girl-girl, either-either)
        if (a.gender !== b.gender) continue;

        // Age ranges overlap if a.from <= b.to AND b.from <= a.to
        if (a.range_from <= b.range_to && b.range_from <= a.range_to) {
          if (!errors.some(e => e.index === bandsWithRanges[j].realIdx && e.message.includes('overlaps'))) {
            errors.push({
              index: bandsWithRanges[j].realIdx,
              message: `Band ${bandsWithRanges[j].bandIdx + 1} overlaps with Band ${bandsWithRanges[i].bandIdx + 1}`,
            });
          }
        }
      }
    }

    return errors;
  }, [entries, isChildrenField]);

  const hasChildrenErrors = childrenErrors.length > 0;

  const handleSave = () => {
    // Validation: at least one entry with count > 0
    const hasValidEntry = entries.some(e => e.count > 0);
    if (!hasValidEntry) {
      notification.warning({
        message: 'Validation',
        description: 'Please enter at least one quota count greater than 0',
      });
      return;
    }

    // Validation: total cannot exceed responses
    if (exceedsTotal) {
      notification.error({
        message: 'Validation Error',
        description: `Total quota (${totalCount}) cannot exceed total responses (${totalResponses})`,
      });
      return;
    }

    // Range field validations
    if (isRangeField) {
      if (hasRangeErrors) {
        notification.error({
          message: 'Range Error',
          description: rangeErrors[0].message,
        });
        return;
      }

      // All bands with count > 0 must have valid from/to
      const invalidBands = entries.filter(e => e.count > 0 && (e.range_from == null || e.range_to == null));
      if (invalidBands.length > 0) {
        notification.error({
          message: 'Validation Error',
          description: 'All bands with a count must have valid From and To values',
        });
        return;
      }
    }

    // Children field validations
    if (isChildrenField && hasChildrenErrors) {
      notification.error({
        message: 'Validation Error',
        description: childrenErrors[0].message,
      });
      return;
    }

    // Filter out zero-count entries and save
    const validEntries = entries
      .filter(e => e.count > 0)
      .map(e => {
        if (isRangeField) {
          return {
            range_from: e.range_from,
            range_to: e.range_to,
            units: e.units,
            label: field === 'income'
              ? `$${e.range_from?.toLocaleString()} - $${e.range_to?.toLocaleString()}`
              : `${e.range_from} - ${e.range_to}`,
            count: e.count,
          };
        }
        if (isChildrenField && e.type === 'have_children') {
          const genderLabel = e.gender === 'boy' ? 'Boy' : e.gender === 'girl' ? 'Girl' : 'Either';
          const unitLabel = e.units === 312 ? 'mos' : 'yrs';
          return {
            type: 'have_children',
            range_from: e.range_from,
            range_to: e.range_to,
            units: e.units,
            gender: e.gender,
            label: `${genderLabel}, ${e.range_from}-${e.range_to} ${unitLabel}`,
            count: e.count,
          };
        }
        if (isChildrenField && e.type === 'no_children') {
          return {
            type: 'no_children',
            condition_code: 167,
            label: 'No Children',
            count: e.count,
          };
        }
        return e;
      });

    onSave({
      field,
      qualification_code: fieldConfig.code,
      entries: validEntries,
    });
  };

  const canSave = entries.some(e => e.count > 0) && !exceedsTotal && !hasRangeErrors && !hasChildrenErrors;

  if (!fieldConfig) return null;

  // Format range header label
  const rangeHeaderLabel = field === 'income'
    ? `INCOME ($${parentRange?.min?.toLocaleString() || 0} to $${parentRange?.max?.toLocaleString() || 0})`
    : `AGE (${parentRange?.min || 0} to ${parentRange?.max || 0})`;

  // Modal width
  const modalWidth = isChildrenField ? 580 : isRangeField ? 600 : 500;

  // Count of "have_children" bands for remove button visibility
  const haveChildrenBandCount = entries.filter(e => e.type === 'have_children').length;

  // Max age based on unit: 17 for years, 47 for months (per PS rules)
  const currentChildUnit = entries.find(e => e.type === 'have_children')?.units || 311;
  const childAgeMax = currentChildUnit === 312 ? 47 : 17;

  return (
    <ThemeModal
      title={existingQuota ? `Edit Quota - ${fieldConfig.label}` : `Add Quota - ${fieldConfig.label}`}
      open={visible}
      onCancel={onClose}
      onOk={handleSave}
      width={modalWidth}
      destroyOnClose
      centered
      okText="Save"
      cancelText="Cancel"
      okButtonProps={{ disabled: !canSave }}
    >
      <QuotaModalContent>
        {!totalResponses && (
          <div className="quota-warning">
            Please set the Responses field first to enable quota validation.
          </div>
        )}

        {isChildrenField ? (
          <>
            <div className="quota-header">
              <span className="quota-label">CHILDREN</span>
              <span className="quota-count-label"># OF COMPLETES</span>
            </div>

            <div className="quota-entries children-entries">
              {/* Have Children bands */}
              {entries.some(e => e.type === 'have_children') && (
                <>
                  <div className="children-section-header">
                    <span className="children-section-label">Have Children</span>
                    <Select
                      className="children-unit-select"
                      value={entries.find(e => e.type === 'have_children')?.units || 311}
                      onChange={handleUnitChange}
                      size="small"
                      popupMatchSelectWidth={false}
                    >
                      <Select.Option value={311}>Years</Select.Option>
                      <Select.Option value={312}>Months</Select.Option>
                    </Select>
                  </div>
                  {entries.map((entry, index) => {
                    if (entry.type !== 'have_children') return null;
                    return (
                      <QuotaChildrenBandRow key={index} $hasError={childrenErrors.some(e => e.index === index)}>
                        <div className="band-age-section">
                          <InputNumber
                            className="age-input"
                            value={entry.range_from}
                            onChange={(v) => handleChildrenBandChange(index, 'range_from', v)}
                            min={0}
                            max={childAgeMax}
                            placeholder="From"
                            controls={false}
                          />
                          <span className="range-separator">to</span>
                          <InputNumber
                            className="age-input"
                            value={entry.range_to}
                            onChange={(v) => handleChildrenBandChange(index, 'range_to', v)}
                            min={0}
                            max={childAgeMax}
                            placeholder="To"
                            controls={false}
                          />
                        </div>
                        <div className="band-gender-section">
                          <Radio.Group
                            className="gender-radio"
                            value={entry.gender}
                            onChange={(e) => handleChildrenBandChange(index, 'gender', e.target.value)}
                            size="small"
                          >
                            <Radio value="boy">Boy</Radio>
                            <Radio value="girl">Girl</Radio>
                            <Radio value="either">Either</Radio>
                          </Radio.Group>
                        </div>
                        <div className="band-count-section">
                          <InputNumber
                            className="count-input"
                            value={entry.count}
                            onChange={(v) => handleCountChange(index, v)}
                            min={0}
                            max={totalResponses || 999999}
                            placeholder="0"
                            controls={false}
                          />
                          {haveChildrenBandCount > 1 && (
                            <button
                              type="button"
                              className="remove-band-btn"
                              onClick={() => handleRemoveChildrenBand(index)}
                            >
                              <CloseOutlined />
                            </button>
                          )}
                        </div>
                      </QuotaChildrenBandRow>
                    );
                  })}
                  <button type="button" className="add-band-btn" onClick={handleAddChildrenBand}>
                    <PlusOutlined /> Add Band
                  </button>
                </>
              )}

              {/* No Children entry */}
              {entries.map((entry, index) => {
                if (entry.type !== 'no_children') return null;
                return (
                  <QuotaEntryRow key="no_children">
                    <span className="entry-label">No Children</span>
                    <InputNumber
                      className="entry-input"
                      value={entry.count}
                      onChange={(v) => handleCountChange(index, v)}
                      min={0}
                      max={totalResponses || 999999}
                      placeholder="0"
                      controls={false}
                    />
                  </QuotaEntryRow>
                );
              })}
            </div>

            {childrenErrors.length > 0 && (
              <div className="quota-error">
                {childrenErrors[0].message}
              </div>
            )}
          </>
        ) : isRangeField ? (
          <>
            <div className="quota-header range-header">
              <span className="quota-label range-col">{rangeHeaderLabel}</span>
              <span className="quota-count-label"># OF COMPLETES</span>
            </div>

            <div className="quota-entries">
              {entries.map((entry, index) => (
                <QuotaRangeBandRow
                  key={index}
                  $hasError={rangeErrors.some(e => e.index === index)}
                >
                  <div className="range-inputs">
                    <InputNumber
                      className="range-input"
                      value={entry.range_from}
                      onChange={(value) => handleRangeFromChange(index, value)}
                      min={index > 0 && entries[index - 1]?.range_to != null
                        ? entries[index - 1].range_to + 1
                        : (parentRange?.min || 0)}
                      max={parentRange?.max}
                      placeholder="From"
                      controls={false}
                      disabled={index > 0 && entries[index - 1]?.range_to != null}
                    />
                    <span className="range-separator">to</span>
                    <InputNumber
                      className="range-input"
                      value={entry.range_to}
                      onChange={(value) => handleRangeToChange(index, value)}
                      min={entry.range_from != null ? entry.range_from : (parentRange?.min || 0)}
                      max={parentRange?.max}
                      placeholder="To"
                      controls={false}
                    />
                  </div>
                  <div className="count-and-actions">
                    <InputNumber
                      className="count-input"
                      value={entry.count}
                      onChange={(value) => handleCountChange(index, value)}
                      min={0}
                      max={totalResponses || 999999}
                      placeholder="0"
                      controls={false}
                    />
                    {entries.length > 1 && (
                      <button
                        type="button"
                        className="remove-band-btn"
                        onClick={() => handleRemoveBand(index)}
                      >
                        <CloseOutlined />
                      </button>
                    )}
                  </div>
                </QuotaRangeBandRow>
              ))}
            </div>

            <button type="button" className="add-band-btn" onClick={handleAddBand}>
              <PlusOutlined /> Add Band
            </button>

            {rangeErrors.length > 0 && (
              <div className="quota-error">
                {rangeErrors[0].message}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="quota-header">
              <span className="quota-label">Option</span>
              <span className="quota-count-label">Count</span>
            </div>

            {showSearch && (
              <Input
                className="quota-search-input"
                placeholder="Search options..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            )}

            <div className="quota-entries">
              {displayEntries.map((entry) => (
                <QuotaEntryRow key={entry.condition_code || entry.label || entry._idx}>
                  <span className="entry-label">{entry.label}</span>
                  <InputNumber
                    className="entry-input"
                    value={entry.count}
                    onChange={(value) => handleCountChange(entry._idx, value)}
                    min={0}
                    max={totalResponses || 999999}
                    placeholder="0"
                    controls={false}
                  />
                </QuotaEntryRow>
              ))}
            </div>
          </>
        )}

        <QuotaTotalRow $isError={exceedsTotal} $isWarning={isUnderTotal && !exceedsTotal}>
          <span className="total-label">Total</span>
          <span className="total-value">
            {totalCount}
            {totalResponses && (
              <span className="total-max"> / {totalResponses}</span>
            )}
          </span>
        </QuotaTotalRow>

        {exceedsTotal && (
          <div className="quota-error">
            Total quota cannot exceed total responses ({totalResponses})
          </div>
        )}

        {isUnderTotal && !exceedsTotal && (
          <div className="quota-info">
            Note: Quota total ({totalCount}) is less than responses ({totalResponses}).
            PureSpectrum allows flexible quota totals.
          </div>
        )}

        <QuotaActions>
          <button type="button" className="action-btn" onClick={handleEvenSplit} disabled={!totalResponses}>
            Even Split
          </button>
          <button type="button" className="action-btn clear-btn" onClick={handleClearAll}>
            Clear All
          </button>
        </QuotaActions>
      </QuotaModalContent>
    </ThemeModal>
  );
};

export default QuotaModal;
